/* The approvals queue.
 *
 * The things a Support Coach genuinely cannot do alone: a price or plan change, a
 * cancellation, a duplicate merge, a lead written off. Every one points at a real
 * record, and APPROVING APPLIES THE CHANGE. Until 2026-09-04 resolving an approval
 * only dropped the row, so "Approve" changed nothing on the record it named.
 */
import { useState } from 'react';
import { useAsync, useSource } from '../data/SourceProvider.js';
import { NoneRow } from '../components/Panel.js';
import { useToast } from '../components/Toast.js';
import { fmt } from '../lib/fmt.js';

const KINDS: Record<string, { label: string; tone: string }> = {
  member_update: { label: 'Change', tone: '' },
  lead_update: { label: 'Lead', tone: 'info' },
  merge: { label: 'Merge', tone: 'warn' },
  cancellation: { label: 'Cancellation', tone: 'risk' },
};

export function Approvals() {
  const source = useSource();
  const toast = useToast();
  const [epoch, setEpoch] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const { data, error } = useAsync(() => source.getApprovals(), [source, epoch]);

  async function decide(id: string, decision: 'approved' | 'rejected') {
    setBusy(id);
    try {
      await source.decideApproval(id, decision);
      toast(decision === 'approved' ? 'Approved, and applied to the record' : 'Rejected', 'good');
      setEpoch((n) => n + 1);
    } catch (e) {
      toast((e as Error).message, 'bad');
    } finally {
      setBusy(null);
    }
  }

  if (error) return <NoneRow>Could not read the queue. {error.message}</NoneRow>;
  if (!data) return <NoneRow>Reading the queue…</NoneRow>;
  if (!data.total) {
    return (
      <section className="panel">
        <NoneRow>All caught up: no changes waiting for approval.</NoneRow>
      </section>
    );
  }

  return (
    <section className="panel">
      <header>
        <h2 className="t-heading-h3">Pending approvals</h2>
        {/* The QUEUE's length, not the page's. The server returns at most a hundred
            and this printed what came back, so an owner with 250 to sign off read
            "100 waiting" and could have emptied the screen with 150 still there. */}
        <span className="eyebrow t-label-xsmall">{fmt.num(data.total)} waiting</span>
      </header>
      {/* A header saying 151 over a table holding 100 is the lead board's problem
          again: the count is right and the page is silent about being a page. The
          oldest come first, so working down the list is the right thing to do - and
          the queue does not end where the table does. */}
      {data.total > data.items.length && (
        <p className="t-caption muted" style={{ marginBottom: 12 }}>
          The {fmt.num(data.items.length)} that have waited longest are below. Sign these
          off and the next {fmt.num(Math.min(data.items.length, data.total - data.items.length))} appear.
        </p>
      )}
      <table className="table">
        <thead>
          <tr className="t-label-small">
            <th /><th>Change</th><th>Who it is about</th><th>Requested</th><th />
          </tr>
        </thead>
        <tbody>
          {data.items.map((a) => {
            const k = KINDS[a.kind] ?? { label: a.kind.replace(/_/g, ' '), tone: '' };
            return (
              <tr key={a.id} style={{ cursor: 'default' }}>
                <td><span className={'badge ' + k.tone + ' t-label-xsmall'}>{k.label}</span></td>
                <td className="t-body-small">{a.summary}</td>
                <td className="t-body-small muted">
                  {(a.payload?.['targetName'] as string | undefined) ?? (a.memberId ? 'A member' : 'A lead')}
                </td>
                <td className="t-caption muted">
                  {fmt.ago(a.requestedAt)}<div>{a.requestedBy}</div>
                </td>
                <td className="right" style={{ whiteSpace: 'nowrap' }}>
                  <button type="button" className="btn primary t-label-small" disabled={busy === a.id}
                    onClick={() => void decide(a.id, 'approved')}>Approve</button>
                  {' '}
                  <button type="button" className="btn t-label-small" disabled={busy === a.id}
                    onClick={() => void decide(a.id, 'rejected')}>Reject</button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}
